import { checkSerialCanUse, checkSerialLastHistoryDetail, getSerialHistories } from '@/apis/serialAPI'
import projectStore from '@/store/project'
import {
  getBarcode,
  getBarcodeByPath,
  getTracking,
  linkingBarcodes,
  pairBarcode,
  recycleMultipleBarcode,
  undoTracking,
  unpairBarcode,
  updateBarcodeTrackingData,
} from '@/utils/api'
import { DEFAULT_ACTIVATION_DATA, LOCAL_STORAGE_ITEM } from '@/utils/constants'
import { ELinkType, EOperatorType } from '@/utils/enums'
import { getCompiledBarcode, mergeTrackingDataWithSerials } from '@/utils/helpers'
import { IActivationData, IBarcode, IManufacturingPlan, ITrackingData } from '@/utils/types/barcodeTypes'
import {
  IBarcodeSavedImageStatus,
  IBarcodeSearchForm,
  IListStringItem,
  ISerialParams,
  ITrackingRequestBody,
} from '@/utils/types/commonTypes'
import { IProject } from '@/utils/types/projectTypes'
import isEmpty from 'lodash/isEmpty'
import {
  ADD_TRACKING,
  BARCODE_ACTION_COMPLETE,
  BARCODE_SELECTED_TYPE,
  BE_LINKED,
  CHECK_SERIAL_DETAIL,
  CHECK_SERIAL_LAST_HISTORY_DETAIL,
  CLEAR_BARCODE,
  CLEAR_ERROR,
  CLEAR_SCANNED_UPLOAD_STATUS,
  ERROR_OCCUR,
  FETCH_BARCODE,
  FETCH_PROJECT_BY_ID,
  GET_SERIAL_HISTORIES,
  GET_TRACKING,
  LINK_TO,
  NUMBER_BARCODE_ACTION_COMPLETE,
  PAIRING,
  RECYCLE_MULTIPLE_BARCODE,
  RESET_ALL_BARCODE_BLOB,
  SAVE_BARCODE,
  SAVE_BARCODE_SEARCH_PARAMS,
  SAVE_SCANNED_BARCODES,
  SAVE_SCANNED_EXTERNAL_ID,
  SAVE_SCANNED_WORK_CENTER_CODE,
  SET_BARCODE_FORM_STEP,
  UNDO_TRACKING,
  UNPAIR,
  UNPAIRING,
  UPDATE_ACTIVATION_DATA_BARCODE,
  UPDATE_CUSTOM_FIELD_OCR_IMAGE_RESULT,
  UPDATE_SCANNED_RESULT,
  UPDATE_SCANNED_UPLOAD_STATUS,
} from './actions'

const enum EActionCompleted {
  ACTIVATED = 'activated',
  TRACKED = 'tracked',
  RECYCLED = 'recycled',
  PAIRED = 'paired',
  UNPAIRED = 'unpaired',
  UNDO_TRACKING = 'undo_tracking',
  LINKED = 'linked',
  EMPTY = '',
}

interface IDefaultActionParams {
  commit: Function
  dispatch: Function
  state: TBarcodeState
}
interface TBarcodeState {
  barcode?: IBarcode | null
  manufacturingPlan?: IManufacturingPlan | null
  error: boolean
  errorMsg: string | number
  actionCompleted: EActionCompleted
  numberOfBCAffected: number
  currentSelectedBarcodeType?: string
  trackedStep: number
  scannedBCPool: IBarcode[]
  scannedBarcodeBlob: Blob[]
  customFieldsOcrImagesBlobs: Record<string, Blob[]>
  scannedBarcodeStatus?: Record<string, IBarcodeSavedImageStatus>
  barcodeSearchParams: {
    condition?: IBarcodeSearchForm
  }
  scannedExternalId: string
  trackingData?: ITrackingData | null
}

export default {
  state: {
    barcode: null,
    manufacturingPlan: null,
    error: false,
    errorMsg: '',
    actionCompleted: '',
    numberOfBCAffected: 0,
    currentSelectedBarcodeType: '',
    trackedStep: 0,
    scannedBCPool: [],
    scannedBarcodeBlob: [],
    customFieldsOcrImagesBlobs: {},
    scannedBarcodeStatus: false,
    barcodeSearchParams: {},
    scannedExternalId: '',
    trackingData: null,
  },
  getters: {
    barcode: (state: TBarcodeState) => {
      return state.barcode
    },
    scannedBarcodeStatus: (state: TBarcodeState) => {
      return state.scannedBarcodeStatus
    },
    manufacturingPlan(state: TBarcodeState) {
      return state.manufacturingPlan
    },
    scannedExternalId: (state: TBarcodeState) => {
      return state.scannedExternalId
    },
    scannedBarcodeBlob: (state: TBarcodeState) => {
      return state.scannedBarcodeBlob
    },
    customFieldsOcrImagesBlobs: (state: TBarcodeState) => {
      return state.customFieldsOcrImagesBlobs
    },
    barcodeExist: (state: TBarcodeState) => {
      return state.barcode !== null
    },
    barcodeType: (state: TBarcodeState) => {
      return state.barcode?.barcodeType
    },
    barcodeActivated: (state: TBarcodeState) => {
      return !!state.barcode?.isActivated
    },
    trackingNumber: (state: TBarcodeState) => {
      return state.barcode?.activationData?.trackingNumber
    },
    externalId: (state: TBarcodeState) => {
      return state.barcode?.activationData?.externalId
    },
    dimension: (state: TBarcodeState) => {
      return state.barcode?.activationData?.dimension
    },
    origin: (state: TBarcodeState) => {
      return state.barcode?.activationData?.origin
    },
    destination: (state: TBarcodeState) => {
      return state.barcode?.activationData?.destination
    },
    searchCondition: (state: TBarcodeState) => {
      return state.barcodeSearchParams
    },
    currentTrackingPoint: (state: TBarcodeState) => {
      return state.barcode?.currentTrackPointKey
    },
    workCenterCode: (state: TBarcodeState) => {
      return state.barcode?.activationData?.customFields?.WRKC.text
    },
    trackingData: (state: TBarcodeState) => {
      return state.trackingData
    },
  },
  mutations: {
    [SAVE_BARCODE]: (state: TBarcodeState, payload?: IBarcode) => {
      if (payload) {
        payload.activationData = { ...(DEFAULT_ACTIVATION_DATA as IActivationData), ...payload.activationData }
        state.barcode = { ...payload }
        state.manufacturingPlan = state.barcode.manufacturingPlan || null
        state.error = false
      } else {
        state.barcode = null
        state.error = true
      }
    },
    [UPDATE_ACTIVATION_DATA_BARCODE]: (state: TBarcodeState, payload?: IActivationData) => {
      if (payload && state.barcode) {
        state.barcode.activationData = { ...state.barcode?.activationData, ...payload }
      }
    },
    [UPDATE_SCANNED_RESULT]: (state: TBarcodeState, payload?: Blob[]) => {
      if (payload && state) {
        state.scannedBarcodeBlob = [...payload]
      }
    },
    [UPDATE_CUSTOM_FIELD_OCR_IMAGE_RESULT]: (state: TBarcodeState, payload?: Record<string, Blob[]>) => {
      if (payload && state) {
        Object.keys(payload).forEach((key) => {
          const blobs = payload[key]
          if (key in state.customFieldsOcrImagesBlobs) {
            state.customFieldsOcrImagesBlobs = {
              ...state.customFieldsOcrImagesBlobs,
              [key]: [...state.customFieldsOcrImagesBlobs[key], ...blobs],
            }
          } else {
            state.customFieldsOcrImagesBlobs = {
              ...state.customFieldsOcrImagesBlobs,
              [key]: blobs,
            }
          }
        })
      }
    },
    [RESET_ALL_BARCODE_BLOB]: (state: TBarcodeState) => {
      state.scannedBarcodeBlob.length = 0
      state.customFieldsOcrImagesBlobs = {}
    },
    [SET_BARCODE_FORM_STEP]: (state: TBarcodeState, step: number) => {
      state.trackedStep = step
    },
    [CLEAR_BARCODE]: (state: TBarcodeState) => {
      state.barcode = null
      state.scannedBarcodeBlob.length = 0
    },
    [BARCODE_ACTION_COMPLETE]: (state: TBarcodeState, action: EActionCompleted = EActionCompleted.EMPTY) => {
      state.actionCompleted = action
    },
    [NUMBER_BARCODE_ACTION_COMPLETE]: (state: TBarcodeState, numberOfBC: number) => {
      state.numberOfBCAffected = numberOfBC
    },
    [BARCODE_SELECTED_TYPE]: (state: TBarcodeState, payload: string) => {
      state.currentSelectedBarcodeType = payload
    },
    [UPDATE_SCANNED_UPLOAD_STATUS]: (state: TBarcodeState, status: Record<string, IBarcodeSavedImageStatus>) => {
      state.scannedBarcodeStatus = { ...state.scannedBarcodeStatus, ...status }
    },
    [CLEAR_SCANNED_UPLOAD_STATUS]: (state: TBarcodeState) => {
      state.scannedBarcodeStatus = {}
    },
    [SAVE_SCANNED_BARCODES]: (state: TBarcodeState, barcodes: IBarcode[]) => {
      state.scannedBCPool = barcodes
    },
    [SAVE_BARCODE_SEARCH_PARAMS]: (state: TBarcodeState, searchParams: { condition: IBarcodeSearchForm }) => {
      state.barcodeSearchParams = { ...searchParams }
    },
    [ERROR_OCCUR]: (state: TBarcodeState, errorMsg: string | number) => {
      state.error = true
      state.errorMsg = errorMsg
    },
    [CLEAR_ERROR]: (state: TBarcodeState) => {
      state.error = false
      state.errorMsg = ''
    },
    [SAVE_SCANNED_EXTERNAL_ID]: (state: TBarcodeState, payload: string) => {
      if (payload) {
        state.scannedExternalId = payload
      } else {
        state.scannedExternalId = ''
      }
    },
    [GET_TRACKING]: (state: TBarcodeState, payload?: ITrackingData) => {
      state.trackingData = payload || null
    },
  },
  actions: {
    [CLEAR_BARCODE]: ({ commit }: { commit: Function }) => {
      commit(CLEAR_BARCODE)
    },
    [SAVE_SCANNED_WORK_CENTER_CODE]: (
      {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        commit,
      }: { commit: Function },
      workCenterCode: string
    ) => {
      localStorage.setItem(LOCAL_STORAGE_ITEM.WORK_CENTER_CODE, workCenterCode)
    },
    [FETCH_BARCODE]: async (
      {
        commit,
        dispatch,
      }: {
        commit: Function
        dispatch: Function
      },
      data: {
        id: string
        path?: string
        externalId?: string
        trackingNumber?: string
      }
    ) => {
      try {
        let result: IBarcode

        if (data.path) {
          result = await getBarcodeByPath(data.id, data.path)
        } else {
          result = await getBarcode(data.id, data.externalId, data.trackingNumber)
        }
        const project = projectStore.state.details as IProject

        // Check current project and the barcode ID. If not matched refetch the project.
        if (result.projectId !== project.id) {
          await dispatch(FETCH_PROJECT_BY_ID, result?.projectId)
          if (isEmpty(project)) {
            throw Error('Project setting not found')
          }
        }
        getCompiledBarcode(result, project)
        commit(SAVE_BARCODE, result)
      } catch (error) {
        commit(SAVE_BARCODE)
        throw Error('manufacturing_plan_read_fail')
      }
    },
    [UNPAIR]: async ({ commit, dispatch, state }: IDefaultActionParams) => {
      if (state.barcode?.id) {
        const result = await unpairBarcode({
          parentBarcodeId: state.barcode?.id,
          isUnpairAll: true,
          unpairChildrenIds: [],
        })
        commit(SAVE_BARCODE, result)
        dispatch(NUMBER_BARCODE_ACTION_COMPLETE, 1)
        dispatch(BARCODE_ACTION_COMPLETE, 'unpaired')
      }
    },

    [BARCODE_ACTION_COMPLETE]: (
      {
        commit,
      }: {
        commit: Function
      },
      action = EActionCompleted.EMPTY
    ) => {
      commit(BARCODE_ACTION_COMPLETE, action)
    },
    [NUMBER_BARCODE_ACTION_COMPLETE]: (
      {
        commit,
      }: {
        commit: Function
      },
      numberOfBC: number
    ) => {
      commit(NUMBER_BARCODE_ACTION_COMPLETE, numberOfBC)
    },
    [UNDO_TRACKING]: async (
      {
        commit,
        dispatch,
      }: {
        commit: Function
        dispatch: Function
        state: TBarcodeState
      },
      payload: {
        barcodeId: string
      }
    ) => {
      await undoTracking(payload.barcodeId)
      const barcode = await getBarcode(payload.barcodeId)
      commit(SAVE_BARCODE, barcode)
      dispatch(NUMBER_BARCODE_ACTION_COMPLETE, 1)
      dispatch(BARCODE_ACTION_COMPLETE, EActionCompleted.UNDO_TRACKING)
    },

    [SAVE_SCANNED_BARCODES]: async (
      {
        commit,
      }: {
        commit: Function
      },
      barcodes: IBarcode[]
    ) => {
      commit(SAVE_SCANNED_BARCODES, barcodes)
    },

    [ADD_TRACKING]: async (
      {
        commit,
        dispatch,
        state,
      }: {
        commit: Function
        dispatch: Function
        state: TBarcodeState
      },
      extParams: Record<string, unknown>
    ) => {
      const id = state.barcode?.id
      const barcodes = state.scannedBCPool
      if (!id || isEmpty(barcodes)) return

     await updateBarcodeTrackingData({
        barcodeIds: [id, ...barcodes.map((bc) => bc.id)],
        ...extParams,
      } as ITrackingRequestBody)
        .then(() => {
          dispatch(NUMBER_BARCODE_ACTION_COMPLETE, barcodes.length + 1)
          dispatch(BARCODE_ACTION_COMPLETE, EActionCompleted.TRACKED)
        })
        .catch((error) => commit(ERROR_OCCUR, error))
    },
    [LINK_TO]: async ({ commit, dispatch, state }: IDefaultActionParams) => {
      const id = state.barcode?.id
      const barcodes = state.scannedBCPool
      if (!id || isEmpty(barcodes)) return

      const data = {
        barcodeId: id,
        targetBarcodeIds: barcodes.filter((bc) => bc.id !== id).map((bc) => bc.id),
      }

      await linkingBarcodes(ELinkType.LINK_TO, data)
        .then(() => {
          dispatch(NUMBER_BARCODE_ACTION_COMPLETE, barcodes.length)
          dispatch(BARCODE_ACTION_COMPLETE, EActionCompleted.LINKED)
        })
        .catch((error) => commit(ERROR_OCCUR, error))
    },
    [BE_LINKED]: async ({ commit, dispatch, state }: IDefaultActionParams) => {
      const id = state.barcode?.id
      const barcodes = state.scannedBCPool
      if (!id || isEmpty(barcodes)) return

      const data = {
        barcodeId: id,
        targetBarcodeIds: barcodes.filter((bc) => bc.id !== id).map((bc) => bc.id),
      }

      await linkingBarcodes(ELinkType.BE_LINKED, data)
        .then(() => {
          dispatch(NUMBER_BARCODE_ACTION_COMPLETE, barcodes.length)
          dispatch(BARCODE_ACTION_COMPLETE, EActionCompleted.LINKED)
        })
        .catch((error) => commit(ERROR_OCCUR, error))
    },
    [PAIRING]: async (
      {
        commit,
        dispatch,
        state,
      }: {
        commit: Function
        dispatch: Function
        state: TBarcodeState
      },
      extParams: Record<string, unknown>
    ) => {
      const id = state.barcode?.id
      const barcodes = state.scannedBCPool
      if (!id || isEmpty(barcodes)) return

      const data = {
        parentBarcodeId: id as string,
        childrenBarcodeIds: barcodes.map((bc) => bc.id),
        ...extParams,
      }
      await pairBarcode(data)
        .then(() => {
          dispatch(NUMBER_BARCODE_ACTION_COMPLETE, barcodes.length)
          dispatch(BARCODE_ACTION_COMPLETE, EActionCompleted.PAIRED)
        })
        .catch((error) => commit(ERROR_OCCUR, error))
    },
    [UNPAIRING]: async (
      {
        commit,
        dispatch,
        state,
      }: {
        commit: Function
        dispatch: Function
        state: TBarcodeState
      },
      extParams: Record<string, unknown>
    ) => {
      const id = state.barcode?.id
      const barcodes = state.scannedBCPool
      if (!id || isEmpty(barcodes)) return

      const data = {
        parentBarcodeId: id as string,
        isUnpairAll: false,

        unpairChildrenIds: barcodes.map((bc) => bc.id),
        isDryRun: false,
        ...extParams,
      }

      await unpairBarcode(data)
        .then(() => {
          dispatch(NUMBER_BARCODE_ACTION_COMPLETE, barcodes.length)
          dispatch(BARCODE_ACTION_COMPLETE, EActionCompleted.UNPAIRED)
        })
        .catch((error) => commit(ERROR_OCCUR, error))
    },
    [SAVE_SCANNED_EXTERNAL_ID]: (
      {
        commit,
      }: {
        commit: Function
      },
      externalId: string
    ) => {
      commit(SAVE_SCANNED_EXTERNAL_ID, externalId)
    },
    [UPDATE_SCANNED_RESULT]: (
      {
        commit,
      }: {
        commit: Function
      },
      listBlob?: Blob[]
    ) => {
      commit(UPDATE_SCANNED_RESULT, listBlob)
    },

    [UPDATE_CUSTOM_FIELD_OCR_IMAGE_RESULT]: (
      {
        commit,
      }: {
        commit: Function
      },
      customFieldsOcrImagesBlobs?: Record<string, Blob[]>
    ) => {
      commit(UPDATE_CUSTOM_FIELD_OCR_IMAGE_RESULT, customFieldsOcrImagesBlobs)
    },

    [RECYCLE_MULTIPLE_BARCODE]: async (
      {
        commit,
        dispatch,
      }: {
        commit: Function
        dispatch: Function
        state: TBarcodeState
      },
      barcodeIds: string[]
    ) => {
      if (isEmpty(barcodeIds)) return
      const data = {
        isDryRun: false,
        barcodeIds: barcodeIds,
      }
      await recycleMultipleBarcode(data)
        .then(() => {
          dispatch(NUMBER_BARCODE_ACTION_COMPLETE, barcodeIds.length)
          dispatch(BARCODE_ACTION_COMPLETE, EActionCompleted.RECYCLED)
        })
        .catch((error) => commit(ERROR_OCCUR, error))
    },
    [SAVE_BARCODE]: async (
      {
        commit,
      }: {
        commit: Function
      },
      payload: IBarcode
    ) => {
      commit(SAVE_BARCODE, payload)
    },
    [CHECK_SERIAL_DETAIL]: async (
      {
        commit,
        state,
      }: {
        commit: Function
        state: TBarcodeState
      },
      { code, operatorType, isEdit }: { code: string, operatorType: EOperatorType, isEdit: boolean }
    ) => {
      try {
        const barcodeId = state.barcode?.id

        if (!barcodeId) {
          throw new Error('Barcode ID is required')
        }

        const response: IListStringItem = await checkSerialCanUse({
          code,
          barcodeId,
          operatorType,
          isEdit,
        })

        return response
      } catch (error) {
        commit(ERROR_OCCUR, error)
        throw error
      }
    },
    [GET_TRACKING]: async (
      {
        commit,
      }: {
        commit: Function
      },
      params: ISerialParams
    ) => {
      try {
        const response = await getTracking(params)
        const processedResponse = mergeTrackingDataWithSerials(response)
        commit(GET_TRACKING, processedResponse.trackingData)
        return processedResponse.trackingData
      } catch (error) {
        commit(ERROR_OCCUR, error)
        throw error
      }
    },
    [CHECK_SERIAL_LAST_HISTORY_DETAIL]: async (
      {
        commit,
      }: {
        commit: Function
      },
      { code }: { code: string }
    ) => {
      try {
        const response = await checkSerialLastHistoryDetail({ code })
        return response
      } catch (error) {
        commit(ERROR_OCCUR, error)
        throw error
      }
    },
    [GET_SERIAL_HISTORIES]: async (
      {
        commit,
      }: {
        commit: Function
      },
      { code }: { code: string }
    ) => {
      try {
        const response = await getSerialHistories(code)
        return response
      } catch (error) {
        commit(ERROR_OCCUR, error)
        throw error
      }
    },
  },
}
